/* .tbl from the storyboard helmet. The table scrolls sideways inside its card. */
.scroll {
  /* The containing block for visually hidden text in cells, so the scroll box clips it too. */
  position: relative;
  overflow-x: auto;
  max-width: 100%;
}

.scroll:focus-visible {
  outline-offset: -2px;
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.caption {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.table th {
  padding: 10px 12px;
  border-bottom: 1px solid var(--border-card);
  color: var(--text-muted);
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: var(--fw-medium);
  letter-spacing: 0.12em;
  text-align: left;
  text-transform: uppercase;
  white-space: nowrap;
}

.table td {
  padding: 13px 12px;
  border-bottom: 1px solid var(--border-hairline);
  vertical-align: middle;
}

/* The table's own rows only: a table opened inside a row's details keeps its own ground. */
.table > tbody > tr:hover > td {
  background: var(--surface-soft);
}

.table .num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.nowrap {
  white-space: nowrap;
}

.table .emptyRow td {
  padding: 28px 12px;
  background: var(--surface-soft);
  color: var(--text-secondary);
  text-align: center;
}

.table tbody tr.emptyRow:hover td {
  background: var(--surface-soft);
}

/* A designed empty state in the empty row brings its own box: no second ground around it. */
.table .emptyRowState td,
.table tbody tr.emptyRowState:hover td {
  padding: 12px 0 0;
  background: transparent;
}

.emptyTitle {
  color: var(--text-heading);
  font-weight: var(--fw-bold);
}

/* A totals row: the sum under the rows, set off by a heavier rule (SL7 "3 people × $1,500"). */
.table .totalRow td {
  border-top: 2px solid var(--brand-navy);
  border-bottom: 0;
  color: var(--text-heading);
  font-weight: var(--fw-bold);
}

.table tfoot tr:hover td {
  background: none;
}

/* A marked row, always with words beside it (a pill, a signed amount): never colour alone. */
.table .toneProblem td,
.table tbody tr.toneProblem:hover td {
  background: var(--status-problem-bg);
}

.table .toneDone td,
.table tbody tr.toneDone:hover td {
  background: var(--status-done-bg);
}

/* Details opened in place (APP-109): the toggle leads the row, as Payroll's does (APP-067 §13),
   and the open row and its details share one ground, with no rule between them. */
.table .toggleCell {
  width: 44px;
  padding-right: 0;
}

.toggle {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--brand-navy);
  cursor: pointer;
}

/* On a card the toggle says what it does: "Show details" with its chevron. */
.toggle.toggleText {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  width: auto;
  min-height: 44px;
  padding: 0 12px 0 0;
  font-family: var(--font-ui);
  font-size: 14px;
  font-weight: var(--fw-semibold);
  text-decoration: underline;
}

.toggle[data-hovered] {
  background: var(--gray-75);
}

.toggle[data-focus-visible] {
  outline: 2px solid var(--focus-ring-color);
  outline-offset: -2px;
}

/* Child combinators: the people table inside the details keeps its own cells. */
.table > tbody > tr.openRow > td,
.table > tbody > tr.openRow:hover > td {
  border-bottom: 0;
  background: var(--brand-cyan-50);
}

.table > tbody > tr.detailRow > td,
.table > tbody > tr.detailRow:hover > td {
  padding: 4px 12px 18px 56px;
  background: var(--brand-cyan-50);
}

/* Sortable headings (APP-149 D3). The heading's button takes its words' look and the cell's side
   padding, so a sortable heading reads as the others do and its whole cell is the 44 px target.
   The active heading is navy and bold with an arrow; aria-sort on the th says the same in words. */
.table th.sortHead {
  padding-block: 0;
}

.sortButton {
  display: inline-flex;
  gap: 6px;
  align-items: center;
  box-sizing: border-box;
  min-width: 44px;
  min-height: 44px;
  margin-inline: calc(-1 * var(--sort-head-pad, 12px));
  padding: 0 var(--sort-head-pad, 12px);
  border: 0;
  border-radius: var(--radius-sm);
  background: none;
  color: inherit;
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  white-space: nowrap;
  cursor: pointer;
}

/* A numeric heading keeps its words over the numbers' right edge: the arrow goes before them. */
.num .sortButton {
  flex-direction: row-reverse;
}

/* The faint mark on headings that can sort: a token colour at 3:1 on white, not an opacity. */
.sortIcon {
  flex: none;
  color: var(--gray-450);
}

.sortButton[data-hovered] {
  color: var(--brand-navy);
}

.sortButton[data-hovered] .sortIcon {
  color: var(--text-secondary);
}

.sortButton[data-focus-visible] {
  outline: 2px solid var(--focus-ring-color);
  outline-offset: -2px;
}

.sortButton.sortActive {
  color: var(--brand-navy);
  font-weight: var(--fw-bold);
}

.sortButton.sortActive .sortIcon {
  color: var(--brand-cyan-600);
}

/* Card rows have no headings to press: "Sort by" sits above the cards instead. */
.sortBy {
  margin-bottom: 12px;
}

.sortByButton {
  display: inline-flex;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  max-width: 100%;
  min-height: 44px;
  padding: 0 10px 0 12px;
  border: 1px solid var(--border-control);
  border-radius: var(--radius-sm);
  background: var(--white);
  color: var(--text-body);
  font-family: var(--font-body);
  font-size: 14px;
  text-align: left;
  cursor: pointer;
}

.sortByButton[data-focus-visible],
.sortByButton[data-pressed] {
  outline: none;
  border-color: var(--brand-cyan);
  box-shadow: var(--focus-halo);
}

.sortByButton svg {
  flex: none;
  color: var(--text-muted);
}
